import { getCSRFToken } from "../shared/htmx-helpers";
import { getActiveEditor, isHistoryBound, markHistoryBound } from "./editor";
import type { CmsEditor, CMSEditorConfig } from "./editor";

// editorHistory owns the in-CMS back/forward machinery (manual pushState +
// upload-safe popstate body swap) and the assistant target-stamp POST. The
// popstate listener is bound ONCE and must reach the LIVE editor instance long
// after this method's `this` went stale — so it resolves the current editor via
// getActiveEditor() (backed by the _activeEditor singleton in editor.ts), and
// guards single-binding through the _historyBound singleton there via
// isHistoryBound()/markHistoryBound(). Those singletons deliberately stay in
// editor.ts; this mixin reaches them through the exported accessors.
export function editorHistory(config: CMSEditorConfig): Partial<CmsEditor> & ThisType<CmsEditor> {
  return {
    // Debounces the on-screen target stamp POST (Task 6.4) — see
    // _scheduleTargetStamp.
    _targetStampTimer: null as ReturnType<typeof setTimeout> | null,

    // ── In-CMS back/forward — upload-safe ────────────────────────────────────
    // We manage history manually instead of HTMX's hx-push-url because HTMX's
    // popstate restore does a raw innerHTML restore that ignores hx-preserve and
    // would kill in-flight uploads. Navigation entries (rail section switch,
    // page-switcher) pushState a {cmsNav:true} marker; the rail/switcher set those
    // (see cmsPushHistoryExpr / cmsTargetClickHandler). On popstate we re-render
    // at the popped URL via an htmx body swap (innerHTML; native hx-preserve
    // keeps the uploader, toasts, SSE alive). HTMX's own popstate handler only acts on state.htmx, so our
    // cmsNav entries never trigger its destructive restore.
    // _editorURL is the editor page the operator is on (cmsSwitchURL's shape:
    // context/slug/locale/active section/preview mode).
    _editorURL(): string {
      const q = (v: string) => encodeURIComponent(v);
      return (
        `${config.baseURL}/edit?context=${q(config.context)}&slug=${q(config.slug)}` +
        `&locale=${q(config.locale)}&section=${q(this.section)}&preview=${q(this.previewMode)}`
      );
    },

    _bindHistory() {
      // Mark the CURRENT entry as a CMS-nav entry. On the initial page load this
      // overwrites any hx-boost state so a later back TO here uses our body swap,
      // not HTMX's restore. No-op once already marked (body swap re-inits land on
      // an entry that pushState already marked).
      if (!(history.state && (history.state as any).cmsNav)) {
        history.replaceState({ cmsNav: true }, "", window.location.href);
      }
      if (isHistoryBound()) return;
      markHistoryBound();
      window.addEventListener("popstate", (e) => {
        // Only handle our own entries; leaving the editor (a non-cmsNav state) is
        // the browser's / HTMX boost's job.
        if (!e.state || !(e.state as any).cmsNav) return;
        const htmx = (window as any).htmx;
        const go = () => {
          if (htmx)
            htmx.ajax("GET", window.location.href, { target: "body", swap: "innerHTML" });
        };
        // Flush a pending debounced edit on the LIVE editor before traversing so
        // an in-flight save isn't lost, then body swap to the popped URL.
        const ed = getActiveEditor();
        // A held-back invalid placeholder edit can't be flushed and the body
        // swap would drop it: stay. The browser already moved the URL, so put
        // the editor's own URL back as a new entry.
        if (ed?._blockOnInvalidPlaceholders?.()) {
          history.pushState({ cmsNav: true }, "", ed._editorURL());
          return;
        }
        if (ed && ed.flushPending) void ed.flushPending().then(go);
        else go();
      });
    },

    // _assistantConfigured gates the target-stamp POST (and mirrors
    // _scheduleAiEditsRefresh's own guard) on the assistant substrate being
    // wired for this website — #cms-ai-edits is mounted only when
    // CMSEditorData.Assistant != nil (Task 6.2's panel). An unconfigured
    // editor has no CMS conversation to stamp a target onto, so every call
    // below is a silent no-op rather than a failing fetch.
    _assistantConfigured(): boolean {
      return !!document.getElementById("cms-ai-edits");
    },

    // _scheduleTargetStamp debounces a POST of the current on-screen target
    // ({context, section, property_slug, area_slug}) to the CMS assistant's
    // per-conversation metadata (design spec §5.4, Task 6.4) — read back by
    // cmsTargetFromConversationMeta to inject the per-turn context summary
    // (jobs/handler_conversation.go) and resolve the write tool's default
    // target when the assistant omits property_id (cms_set_content_field.go).
    // Called on every rail-section change (the 'section' $watch above) and
    // once on load/target-switcher navigation (init() calls this directly —
    // a target-switcher change body swaps to a whole new page, re-running
    // init() with fresh config, so the $watch alone wouldn't cover it).
    _scheduleTargetStamp() {
      if (!this._assistantConfigured()) return;
      if (this._targetStampTimer) clearTimeout(this._targetStampTimer);
      this._targetStampTimer = setTimeout(() => void this._stampTarget(), 400);
    },
    async _stampTarget() {
      try {
        await fetch(`${config.baseURL}/api/assistant/target`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({
            context: config.context,
            section: this.section,
            // config.slug is the on-screen page's slug — the ACCOMMODATION slug
            // for property pages, the AREA slug for an area page. Send it under
            // the key the backend validates against the matching roster
            // (properties vs. the website's linked areas), so an area's identity
            // (area_id/area_slug) actually lands in cms_target instead of being
            // dropped as a non-matching property_slug.
            property_slug: config.context === "area" ? "" : config.slug,
            area_slug: config.context === "area" ? config.slug : "",
          }),
        });
      } catch {
        // Best-effort background stamp: a failed POST just means the next
        // turn's context summary lags one page behind — never surfaced to
        // the operator (no toast, matches the endpoint's own no-toast 204).
      }
    },
  };
}
